import { createContext, useCallback, useContext, useSyncExternalStore } from 'react'
import type { Node, NodeId } from '@solar/core'
import type { ClientStore } from '../client/store.ts'
import type { SyncStatus } from '../client/sync-client.ts'

export const StoreContext = createContext<ClientStore | null>(null)

export function useStore(): ClientStore {
  const s = useContext(StoreContext)
  if (!s) throw new Error('no store')
  return s
}

/** Re-renders when this node's record is replaced. */
export function useNode(id: NodeId | null): Node | undefined {
  const s = useStore()
  const sub = useCallback((cb: () => void) => (id ? s.subscribeNode(id, cb) : () => {}), [s, id])
  const get = (): Node | undefined => (id ? s.g.get(id) : undefined)
  return useSyncExternalStore(sub, get, get)
}

/** Re-renders on structural changes (create, move, delete, collapse). */
export function useStructure(): number {
  const s = useStore()
  return useSyncExternalStore(s.subscribeStructure, s.getStructureVersion, s.getStructureVersion)
}

/** Re-renders when the caret, selection or options change. */
export function useUi(): number {
  const s = useStore()
  return useSyncExternalStore(s.subscribeUi, s.getUiVersion, s.getUiVersion)
}

export function useSyncStatus(): SyncStatus {
  const s = useStore()
  return useSyncExternalStore(s.sync.subscribeStatus, () => s.sync.status)
}
